import { apiClient } from '@/shared/lib/apiClient';
import type { ApiResponse } from '@/types';
import type {
  CreditsSource,
  CreditsTransactionType,
  MerchStatus,
  Paginated,
  PhotoStatus,
  PhotoType,
  ReferralSource,
  ReferralStatus,
  RewardApprovalMode,
  RewardRepeatability,
  RewardStatus,
  RewardTrigger,
} from '@/features/guide-rewards/types';

export interface GuideSummary {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  guideId: string | null;
  profileId: string | null;
  profileStatus: string | null;
}

export interface AdminRewardActivity {
  _id: string;
  key: string;
  title: string;
  description: string;
  trigger: RewardTrigger;
  credits: number;
  approvalMode: RewardApprovalMode;
  repeatability: RewardRepeatability;
  maxPerGuide: number | null;
  criteria: { bookings?: number; travellerPhotos?: number };
  campaignId: string | null;
  isActive: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface AdminCampaign {
  _id: string;
  name: string;
  description?: string;
  startAt?: string | null;
  endAt?: string | null;
  budgetCredits?: number | null;
  issuedCredits: number;
  isActive: boolean;
  createdAt: string;
}

export interface AdminReward {
  _id: string;
  guideUserId: string;
  activityKey: string;
  achievementKey: string;
  credits: number;
  status: RewardStatus;
  needsReview?: boolean;
  reason?: string;
  decidedAt?: string;
  createdAt: string;
  snapshot: {
    activityTitle: string;
    trigger: RewardTrigger;
    credits: number;
    approvalMode: RewardApprovalMode;
    criteria: { bookings?: number; travellerPhotos?: number };
    campaignName?: string | null;
    evidence: Record<string, unknown>;
  };
  guide: GuideSummary | null;
}

export interface AuditEntry {
  _id: string;
  action: string;
  entityType: string;
  entityId?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  reason?: string;
  actorName: string | null;
  guideName?: string | null;
  createdAt: string;
}

export interface AdminRewardDetail {
  reward: AdminReward;
  guide: GuideSummary | null;
  referral:
    | (Record<string, unknown> & {
        _id: string;
        status: ReferralStatus;
        source: ReferralSource;
        code: string;
        createdAt: string;
        approvedAt?: string;
        referred: Partial<GuideSummary> & { name: string };
      })
    | null;
  transactions: { _id: string; type: CreditsTransactionType; amount: number; createdAt: string; description: string }[];
  history: AuditEntry[];
}

export interface AdminReferral {
  id: string;
  code: string;
  source: ReferralSource;
  status: ReferralStatus;
  createdAt: string;
  onboardingCompletedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  referrer: GuideSummary | null;
  referred: Partial<GuideSummary> & { name: string };
  reward: { _id: string; status: RewardStatus; credits: number } | null;
}

export interface AdminPhoto {
  id: string;
  type: PhotoType;
  bookingId: string | null;
  status: PhotoStatus;
  rejectionReason: string | null;
  checks: { name: string; passed: boolean | null; detail?: string }[];
  createdAt: string;
  url: string;
  fullUrl: string;
  guide: { _id: string; firstName?: string; lastName?: string; email?: string } | null;
  width?: number;
  height?: number;
  bytes: number;
  consentConfirmedAt: string | null;
  attempts: number;
  validationProvider: string | null;
}

export interface AdminMerchRequest {
  _id: string;
  size: string;
  status: MerchStatus;
  address: { line1: string; line2?: string; city: string; state: string; pincode: string };
  contactPhone: string;
  instructions?: string;
  dispatch?: { courier?: string; trackingId?: string; dispatchedAt?: string };
  statusHistory: { status: MerchStatus; at: string; note?: string }[];
  guideUserId: { _id: string; firstName?: string; lastName?: string; email?: string; phone?: string } | null;
  createdAt: string;
}

export interface MerchInventory {
  _id: string;
  sku: string;
  name: string;
  sizes: { size: string; stock: number }[];
  sizeChartNote?: string;
  isActive: boolean;
}

export interface AdminTransaction {
  _id: string;
  guideUserId: string;
  type: CreditsTransactionType;
  source: CreditsSource;
  amount: number;
  description: string;
  idempotencyKey: string;
  createdAt: string;
  guide: GuideSummary | null;
}

export interface GrowthAnalytics {
  totals: {
    totalReferred: number;
    approvedReferrals: number;
    conversionRate: number;
    creditsIssued: number;
    creditsReversed: number;
    creditsAdjusted: number;
    pendingCredits: number;
    pendingRewards: number;
    approvedGuides: number;
  };
  referralsByStatus: Partial<Record<ReferralStatus, number>>;
  monthlyReferrals: { month: string; total: number; approved: number }[];
  activities: {
    key: string;
    title: string;
    trigger: RewardTrigger;
    isActive: boolean;
    credited: number;
    pending: number;
    rejected: number;
    reversed: number;
    creditsIssued: number;
    completionRate: number;
  }[];
  photos: { type: PhotoType; status: PhotoStatus; count: number }[];
  merchandise: { size: string; status: MerchStatus; count: number }[];
  flaggedRewards: number;
}

export interface GrowthOverview {
  queues: { pendingRewards: number; photosToReview: number; merchToReview: number; flaggedRewards: number };
  outbox: Partial<Record<'PENDING' | 'PROCESSING' | 'DONE' | 'DEAD', number>>;
  totals: GrowthAnalytics['totals'];
}

export interface ReconcileResult {
  stored: { available: number; lifetimeEarned: number };
  ledger: { available: number; lifetimeEarned: number };
  drift: { available: number; lifetimeEarned: number };
  inSync: boolean;
  applied: boolean;
}

type Query = Record<string, string | number | boolean | undefined>;
const clean = (query: Query) =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ''));
const unwrap = <T>(response: { data: ApiResponse<T> }) => response.data.data as T;
const base = '/admin/growth';

export const adminGrowthApi = {
  overview: async () => unwrap(await apiClient.get<ApiResponse<GrowthOverview>>(`${base}/overview`)),
  attention: async () => unwrap(await apiClient.get<ApiResponse<{ count: number }>>(`${base}/attention`)),
  analytics: async () => unwrap(await apiClient.get<ApiResponse<GrowthAnalytics>>(`${base}/analytics`)),

  referrals: async (q: Query) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<AdminReferral>>>(`${base}/referrals`, { params: clean(q) })),

  rewards: async (q: Query) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<AdminReward>>>(`${base}/rewards`, { params: clean(q) })),
  reward: async (id: string) => unwrap(await apiClient.get<ApiResponse<AdminRewardDetail>>(`${base}/rewards/${id}`)),
  approveReward: async (id: string, note?: string) =>
    unwrap(await apiClient.post<ApiResponse<AdminReward>>(`${base}/rewards/${id}/approve`, { note })),
  rejectReward: async (id: string, reason: string) =>
    unwrap(await apiClient.post<ApiResponse<AdminReward>>(`${base}/rewards/${id}/reject`, { reason })),
  reverseReward: async (id: string, reason: string) =>
    unwrap(await apiClient.post<ApiResponse<AdminReward>>(`${base}/rewards/${id}/reverse`, { reason })),

  activities: async () => unwrap(await apiClient.get<ApiResponse<AdminRewardActivity[]>>(`${base}/activities`)),
  createActivity: async (input: Partial<AdminRewardActivity>) =>
    unwrap(await apiClient.post<ApiResponse<AdminRewardActivity>>(`${base}/activities`, input)),
  updateActivity: async (id: string, input: Partial<AdminRewardActivity>) =>
    unwrap(await apiClient.patch<ApiResponse<AdminRewardActivity>>(`${base}/activities/${id}`, input)),

  campaigns: async () => unwrap(await apiClient.get<ApiResponse<AdminCampaign[]>>(`${base}/campaigns`)),
  createCampaign: async (input: Partial<AdminCampaign>) =>
    unwrap(await apiClient.post<ApiResponse<AdminCampaign>>(`${base}/campaigns`, input)),
  updateCampaign: async (id: string, input: Partial<AdminCampaign>) =>
    unwrap(await apiClient.patch<ApiResponse<AdminCampaign>>(`${base}/campaigns/${id}`, input)),

  photos: async (q: Query) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<AdminPhoto>>>(`${base}/photos`, { params: clean(q) })),
  approvePhoto: async (id: string, note?: string) =>
    unwrap(await apiClient.post<ApiResponse<unknown>>(`${base}/photos/${id}/approve`, { note })),
  rejectPhoto: async (id: string, reason: string) =>
    unwrap(await apiClient.post<ApiResponse<unknown>>(`${base}/photos/${id}/reject`, { reason })),
  retryPhoto: async (id: string) => unwrap(await apiClient.post<ApiResponse<unknown>>(`${base}/photos/${id}/retry`)),

  merchRequests: async (q: Query) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<AdminMerchRequest>>>(`${base}/merch/requests`, { params: clean(q) })),
  merchExport: async () => unwrap(await apiClient.get<ApiResponse<AdminMerchRequest[]>>(`${base}/merch/export`)),
  transitionMerch: async (id: string, input: { status: MerchStatus; note?: string; courier?: string; trackingId?: string }) =>
    unwrap(await apiClient.post<ApiResponse<AdminMerchRequest>>(`${base}/merch/requests/${id}/status`, input)),
  inventory: async () => unwrap(await apiClient.get<ApiResponse<MerchInventory | null>>(`${base}/merch/inventory`)),
  updateInventory: async (input: { name?: string; sizes: { size: string; stock: number }[]; sizeChartNote?: string; isActive?: boolean }) =>
    unwrap(await apiClient.put<ApiResponse<MerchInventory>>(`${base}/merch/inventory`, input)),

  transactions: async (q: Query) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<AdminTransaction>>>(`${base}/credits/transactions`, { params: clean(q) })),
  adjust: async (input: { guideId: string; amount: number; reason: string }) =>
    unwrap(await apiClient.post<ApiResponse<AdminTransaction>>(`${base}/credits/adjust`, input)),
  reconcile: async (guideUserId: string, apply: boolean) =>
    unwrap(await apiClient.post<ApiResponse<ReconcileResult>>(`${base}/credits/reconcile/${guideUserId}`, { apply })),

  auditLogs: async (q: Query) =>
    unwrap(await apiClient.get<ApiResponse<Paginated<AuditEntry>>>(`${base}/audit-logs`, { params: clean(q) })),
};

export const adminGrowthKeys = {
  all: ['admin-growth'] as const,
  overview: () => [...adminGrowthKeys.all, 'overview'] as const,
  attention: () => [...adminGrowthKeys.all, 'attention'] as const,
  analytics: () => [...adminGrowthKeys.all, 'analytics'] as const,
  referrals: (q: Query) => [...adminGrowthKeys.all, 'referrals', q] as const,
  rewards: (q: Query) => [...adminGrowthKeys.all, 'rewards', q] as const,
  reward: (id: string) => [...adminGrowthKeys.all, 'reward', id] as const,
  activities: () => [...adminGrowthKeys.all, 'activities'] as const,
  campaigns: () => [...adminGrowthKeys.all, 'campaigns'] as const,
  photos: (q: Query) => [...adminGrowthKeys.all, 'photos', q] as const,
  merch: (q: Query) => [...adminGrowthKeys.all, 'merch', q] as const,
  inventory: () => [...adminGrowthKeys.all, 'inventory'] as const,
  transactions: (q: Query) => [...adminGrowthKeys.all, 'transactions', q] as const,
  audit: (q: Query) => [...adminGrowthKeys.all, 'audit', q] as const,
};

export function adminErrorMessage(error: unknown): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    'Something went wrong. Please try again.'
  );
}
